import Heading from "@afa-shared/afa-components/dist/Heading";
import Icon from "@afa-shared/afa-components/dist/Icon";
import Text from "@afa-shared/afa-components/dist/Text";
import { ContentLink } from "@components/Link/ContentLink";
import IvSmallTeaserImage from "@components/Medias/Image/IvSmallTeaserImage";
import { replaceImageVaultBasePath } from "@utils/urlResolver";
import clsx from "clsx";

import { Context } from "types/context/Context";

import teaserListStyles from "./TeaserList.module.css";
import {
  TeaserListBlockType_teaserListBlock,
  TeaserListBlockType_teaserListBlock_listItemsCollection_items,
} from "./queries/contentfulTypes/TeaserListBlockType";

interface TeaserBlockData extends TeaserListBlockType_teaserListBlock_listItemsCollection_items {
  teaserBlockUrl: string;
  fileSize: string;
  fileName: string;
  fileExtension: string;
  id: string;
}

interface ITeaserList {
  blockData: TeaserListBlockType_teaserListBlock;
  context: Context;
}

const TeaserList = ({ blockData: teaserList, context }: ITeaserList): React.ReactNode => {
  const borderClass = context?.sectionBlock?.coloredSection ? "" : teaserListStyles.border;
  const sectionHeading = context?.sectionBlock?.sectionBlockHeading ?? teaserList?.heading ?? "";

  /**
   * Determine link type based on content type
   * News and Event pages should be tracked as "news", others as "teaser"
   */
  const getLinkType = (item: TeaserBlockData): "news" | "teaser" => {
    const contentType =
      item?.link?.__typename === "FileBlock" ? null : item?.link?.content?.__typename;

    if (contentType === "Newspage" || contentType === "Eventpage") {
      return "news";
    }

    const isNewsSection = /(aktuellt|senaste\s*nytt)/i.test(sectionHeading);
    const isFileLink = item?.link?.__typename === "FileBlock";

    if (isNewsSection && !isFileLink) {
      return "news";
    }

    return "teaser";
  };

  const renderCard = (item: TeaserBlockData, showTeaserText: boolean) => {
    const url = replaceImageVaultBasePath(item?.teaserBlockUrl);
    //Temp solution for nofollow rel on "intressant vetande"-teaser
    const tempNofollow = item?.sys?.id === "5L39saocxuRQdvNuNYuTXc";
    const teaserIsFile = item?.fileName !== undefined;
    const linkText = teaserIsFile
      ? item?.linkText
        ? item?.linkText + " (" + item?.fileSize + ")"
        : "Ladda ner (" + item?.fileSize + ")"
      : (item?.linkText ?? "Läs mer");
    const linkIsExternal = item?.externalLink && !item?.externalLink?.includes("om-oss/pressrum");
    return (
      <ContentLink
        href={url}
        className={teaserListStyles.cardLink}
        noFollow={tempNofollow}
        trackingData={{
          sectionTitle:
            context?.sectionBlock?.sectionBlockHeading ??
            teaserList?.heading ??
            context?.pageHeading ??
            undefined,
          sectionBlockIndex: context?.sectionBlock?.sectionBlockIndex ?? undefined,
          linkType: getLinkType(item),
        }}
        contentText={item.heading}
      >
        <div className={teaserListStyles.imageWrapper} aria-hidden>
          <IvSmallTeaserImage image={item?.ivImage?.[0]} />
        </div>
        <div className={teaserListStyles.labelWrapper}>
          <Heading
            variant={context?.heading?.childLevel}
            children={<>{item.heading}</>}
            className={teaserListStyles.label}
            removeMargin
          />
          {showTeaserText && <Text variant="paragraph" children={item?.text} removeMargin />}
          <Text variant="link" className={teaserListStyles.link}>
            {!linkIsExternal && !teaserIsFile && (
              <Icon name="arrow_forward" size="sm" className={teaserListStyles.internalLinkIcon} />
            )}
            {teaserIsFile && (
              <Icon name="file_download" size="sm" className={teaserListStyles.fileLinkIcon} />
            )}
            {linkText}
            {linkIsExternal && (
              <Icon
                name="open_in_new"
                size="sm"
                fill="#020678"
                aria-label="Extern länk"
                className={teaserListStyles.externalLinkIcon}
              />
            )}
          </Text>
        </div>
      </ContentLink>
    );
  };

  return (
    <div
      className={teaserListStyles.teaserList}
      data-testid="teaser-list"
      data-contentful-entry-id={teaserList?.sys?.id}
      data-contentful-field-id="name"
    >
      <div className={teaserListStyles.listWrapper}>
        <Heading
          variant={context?.heading?.parentLevel}
          removeMargin
          children={teaserList?.heading}
        />
        <ul className={clsx(teaserListStyles.list, borderClass)}>
          {teaserList?.listItemsCollection.items?.map((item: TeaserBlockData, index) => {
            return (
              <li className={clsx(teaserListStyles.listItem, teaserListStyles.column)} key={index}>
                {renderCard(item, teaserList?.showTeaserText)}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default TeaserList;
